export interface ProductDetail {
  id: number;
  name: string;
  price: number;
  description: string;
  highlights: string[];
  materials: string;
  fit: string;
  care: string;
  images: string[];
  category: 'apparel' | 'footwear' | 'accessories';
  subcategory: string;
  sizes: string[];
  colors: Array<{ name: string; hex: string }>;
  inStock: boolean;
  sku: string;
}

export const productDetails: ProductDetail[] = [
  // ─── APPAREL ──────────────────────────────────────────────
  {
    id: 1,
    name: 'Varsity Legacy Jacket',
    price: 189,
    description: 'The Varsity Legacy Jacket is our signature piece. Built with premium wool body and genuine leather sleeves, this jacket represents everything BillPlay stands for: quality, heritage, and bold style. Perfect for making a statement while staying comfortable.',
    highlights: [
      'Premium wool blend body with genuine leather sleeves',
      'Embroidered chenille patches and custom snap buttons',
      'Quilted satin lining for warmth and comfort',
      'Ribbed collar, cuffs, and hem for classic varsity fit',
      'Limited production run — statement piece'
    ],
    materials: '80% Wool, 20% Nylon body / 100% Genuine Leather sleeves / Quilted polyester lining',
    fit: 'Classic varsity fit with slightly relaxed shoulders. True to size. Model is 6\'1" wearing size L.',
    care: 'Professional dry clean only. Store on padded hanger. Avoid prolonged sun exposure.',
    images: [
      'https://public.readdy.ai/ai/img_res/547a98b3573ad2c6eef1bf9e076b1c13.jpg',
      'https://readdy.ai/api/search-image?query=premium%20black%20varsity%20jacket%20back%20view%20showing%20embroidered%20patches%20and%20details%20on%20model%20in%20studio%20with%20dramatic%20lighting%20clean%20dark%20background%20editorial%20fashion%20photography%20style&width=800&height=1000&seq=varsity-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20shot%20of%20varsity%20jacket%20leather%20sleeve%20and%20wool%20body%20texture%20with%20stitching%20and%20snap%20buttons%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=varsity-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=premium%20varsity%20jacket%20hanging%20on%20minimalist%20rack%20showing%20full%20silhouette%20with%20dramatic%20studio%20lighting%20clean%20dark%20background%20editorial%20fashion%20photography%20style&width=800&height=1000&seq=varsity-detail-4&orientation=portrait'
    ],
    category: 'apparel',
    subcategory: 'jackets',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    colors: [
      { name: 'Black/White', hex: '#000000' },
      { name: 'Navy/Cream', hex: '#1a1a2e' }
    ],
    inStock: true,
    sku: 'BP-VLJ-BW-001'
  },
  {
    id: 2,
    name: 'Northside Bomber',
    price: 165,
    description: 'Born from the streets of Northside Kalamazoo, the Northside Bomber is a clean, versatile essential with premium construction and a silhouette that works for every occasion. Minimal branding, maximum impact.',
    highlights: [
      'Lightweight premium nylon shell with satin lining',
      'Ribbed collar, cuffs, and hem for classic bomber fit',
      'Two deep side zip pockets and interior chest pocket',
      'Embroidered BillPlay logo on chest',
      'Water-resistant finish'
    ],
    materials: '100% Nylon shell / 100% Polyester satin lining',
    fit: 'Relaxed fit through the body. Model is 6\'0" wearing size M. Size up for a more oversized look.',
    care: 'Machine wash cold on gentle cycle. Hang dry. Do not iron directly on lining.',
    images: [
      'https://readdy.ai/api/search-image?query=black%20premium%20bomber%20jacket%20with%20minimal%20white%20embroidered%20logo%20front%20view%20on%20model%20in%20urban%20setting%20dramatic%20lighting%20dark%20background%20editorial%20streetwear%20fashion%20photography&width=800&height=1000&seq=bomber-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20satin%20bomber%20jacket%20back%20view%20on%20model%20showing%20full%20silhouette%20in%20studio%20with%20dramatic%20lighting%20dark%20background%20editorial%20fashion%20photography%20streetwear%20style&width=800&height=1000&seq=bomber-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20of%20bomber%20jacket%20ribbed%20cuff%20and%20nylon%20texture%20with%20zipper%20and%20pocket%20details%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography%20high%20detail&width=800&height=1000&seq=bomber-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20bomber%20jacket%20hanging%20on%20minimalist%20rack%20full%20length%20view%20dramatic%20studio%20lighting%20clean%20dark%20background%20editorial%20fashion%20product%20photography&width=800&height=1000&seq=bomber-detail-4&orientation=portrait'
    ],
    category: 'apparel',
    subcategory: 'jackets',
    sizes: ['S', 'M', 'L', 'XL'],
    colors: [
      { name: 'Black', hex: '#000000' },
      { name: 'Charcoal', hex: '#3a3a3a' }
    ],
    inStock: true,
    sku: 'BP-NBB-BK-002'
  },
  {
    id: 3,
    name: 'Statement Puffer',
    price: 210,
    description: 'The Statement Puffer is built for those who refuse to blend in. Oversized silhouette, bold design, and premium down-alternative fill make this jacket a cold-weather essential you\'ll reach for every season.',
    highlights: [
      'Oversized premium puffer silhouette',
      'Down-alternative fill for superior warmth',
      'Heavy-duty metal zipper with storm flap',
      'Interior fleece-lined pockets',
      'High-neck stand collar for wind protection'
    ],
    materials: '100% Nylon shell / Down-alternative polyester fill / Polyester lining',
    fit: 'Intentionally oversized. Size down for a fitted look. Model is 5\'11" wearing size L.',
    care: 'Machine wash cold, gentle cycle. Tumble dry low with dryer balls to restore loft.',
    images: [
      'https://readdy.ai/api/search-image?query=oversized%20black%20puffer%20jacket%20with%20bold%20design%20front%20view%20on%20model%20in%20urban%20setting%20with%20dramatic%20lighting%20clean%20dark%20background%20premium%20streetwear%20editorial%20photography%20style&width=800&height=1000&seq=puffer-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=oversized%20black%20puffer%20jacket%20side%20view%20showing%20silhouette%20and%20volume%20on%20model%20in%20studio%20with%20dramatic%20lighting%20dark%20background%20editorial%20fashion%20photography%20style&width=800&height=1000&seq=puffer-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20puffer%20jacket%20quilting%20zipper%20and%20collar%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography%20high%20detail%20texture&width=800&height=1000&seq=puffer-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20oversized%20puffer%20jacket%20hanging%20on%20minimalist%20rack%20showing%20full%20volume%20and%20silhouette%20dramatic%20studio%20lighting%20clean%20dark%20background%20editorial%20fashion%20photography&width=800&height=1000&seq=puffer-detail-4&orientation=portrait'
    ],
    category: 'apparel',
    subcategory: 'outerwear',
    sizes: ['M', 'L', 'XL', '2XL'],
    colors: [
      { name: 'All Black', hex: '#000000' }
    ],
    inStock: true,
    sku: 'BP-SPF-BK-003'
  },
  {
    id: 4,
    name: 'Culture Hoodie',
    price: 85,
    description: 'A staple piece for the culture. The Culture Hoodie is crafted from heavyweight French terry cotton for a premium feel that gets better with every wash. Clean lines, bold logo, and an everyday fit that never misses.',
    highlights: [
      'Heavyweight 400gsm French terry cotton blend',
      'Double-lined hood with flat drawcords',
      'Kangaroo front pocket with clean interior finish',
      'Ribbed cuffs and hem for structured fit',
      'Embroidered BillPlay chest logo'
    ],
    materials: '80% Cotton, 20% Polyester — 400gsm French terry',
    fit: 'Relaxed fit. True to size. Model is 6\'0" wearing size L.',
    care: 'Machine wash cold, inside out. Tumble dry low. Do not bleach.',
    images: [
      'https://readdy.ai/api/search-image?query=premium%20black%20heavyweight%20hoodie%20with%20minimal%20white%20embroidered%20logo%20front%20view%20on%20model%20in%20urban%20setting%20with%20dramatic%20lighting%20dark%20background%20editorial%20streetwear%20fashion%20photography&width=800&height=1000&seq=hoodie-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20premium%20hoodie%20back%20view%20showing%20clean%20silhouette%20and%20hood%20on%20model%20in%20studio%20with%20dramatic%20lighting%20dark%20background%20editorial%20fashion%20photography%20streetwear%20style&width=800&height=1000&seq=hoodie-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20hoodie%20ribbed%20cuff%20kangaroo%20pocket%20and%20cotton%20texture%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography%20high%20detail&width=800&height=1000&seq=hoodie-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20heavyweight%20hoodie%20hanging%20on%20minimalist%20rack%20showing%20full%20silhouette%20with%20dramatic%20studio%20lighting%20clean%20dark%20background%20editorial%20fashion%20product%20photography&width=800&height=1000&seq=hoodie-detail-4&orientation=portrait'
    ],
    category: 'apparel',
    subcategory: 'hoodies',
    sizes: ['S', 'M', 'L', 'XL', '2XL', '3XL'],
    colors: [
      { name: 'Black', hex: '#000000' },
      { name: 'Ash Gray', hex: '#6b6b6b' },
      { name: 'White', hex: '#f5f5f5' }
    ],
    inStock: true,
    sku: 'BP-CHD-BK-004'
  },
  {
    id: 5,
    name: 'Unity Pullover',
    price: 95,
    description: 'The Unity Pullover is more than a hoodie — it\'s a statement of solidarity. A bold graphic across the back connects you to the BillPlay community, while the heavyweight construction keeps you warm and looking right.',
    highlights: [
      'Heavyweight 420gsm fleece interior for superior warmth',
      'Bold full-back graphic screened in water-based ink',
      'Double-needle stitching for long-lasting durability',
      'Cross-grain ribbing at waistband and cuffs',
      'Boxy oversized fit with dropped shoulders'
    ],
    materials: '80% Cotton, 20% Polyester — 420gsm fleece',
    fit: 'Oversized boxy fit. Size down for a relaxed fit. Model is 6\'1" wearing size L.',
    care: 'Machine wash cold, inside out. Tumble dry low. Do not iron over graphics.',
    images: [
      'https://readdy.ai/api/search-image?query=oversized%20black%20pullover%20hoodie%20with%20bold%20white%20back%20graphic%20on%20model%20in%20urban%20setting%20dramatic%20lighting%20dark%20background%20editorial%20streetwear%20fashion%20photography&width=800&height=1000&seq=pullover-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20oversized%20pullover%20hoodie%20front%20view%20showing%20boxy%20silhouette%20on%20model%20in%20studio%20with%20dramatic%20lighting%20dark%20background%20editorial%20fashion%20photography%20streetwear&width=800&height=1000&seq=pullover-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20pullover%20hoodie%20fleece%20texture%20and%20ribbed%20waistband%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography%20high%20detail&width=800&height=1000&seq=pullover-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20boxy%20pullover%20hoodie%20hanging%20on%20minimalist%20rack%20showing%20full%20silhouette%20with%20dramatic%20studio%20lighting%20clean%20dark%20background%20editorial%20fashion%20photography&width=800&height=1000&seq=pullover-detail-4&orientation=portrait'
    ],
    category: 'apparel',
    subcategory: 'hoodies',
    sizes: ['M', 'L', 'XL', '2XL'],
    colors: [
      { name: 'Black', hex: '#000000' },
      { name: 'Charcoal', hex: '#3a3a3a' }
    ],
    inStock: true,
    sku: 'BP-UPO-BK-005'
  },
  {
    id: 6,
    name: 'Northside Tee',
    price: 45,
    description: 'Repping where it all started. The Northside Tee is a heavyweight essential with a clean graphic that tells the story of the brand\'s roots. Wear it as a tribute to real culture, real community, and real style.',
    highlights: [
      'Heavyweight 250gsm 100% ringspun cotton',
      'Oversized boxy cut with dropped shoulders',
      'Northside-inspired screen print graphic',
      'Reinforced double-needle hem and sleeves',
      'Preshrunk to maintain shape wash after wash'
    ],
    materials: '100% Ringspun cotton — 250gsm',
    fit: 'Oversized fit. True to size or size down for a relaxed fit. Model is 6\'0" wearing size L.',
    care: 'Machine wash cold, inside out. Tumble dry low. Do not bleach or iron over print.',
    images: [
      'https://readdy.ai/api/search-image?query=premium%20black%20heavyweight%20t-shirt%20with%20white%20northside%20graphic%20print%20front%20view%20on%20model%20in%20urban%20setting%20dramatic%20lighting%20dark%20background%20editorial%20streetwear%20photography&width=800&height=1000&seq=northtee-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20heavyweight%20t-shirt%20back%20view%20showing%20clean%20silhouette%20on%20model%20in%20studio%20with%20dramatic%20lighting%20dark%20background%20editorial%20fashion%20photography%20streetwear%20style&width=800&height=1000&seq=northtee-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20t-shirt%20graphic%20print%20and%20cotton%20texture%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography%20high%20detail&width=800&height=1000&seq=northtee-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20heavyweight%20t-shirt%20hanging%20on%20minimalist%20rack%20showing%20full%20silhouette%20with%20dramatic%20studio%20lighting%20clean%20dark%20background%20editorial%20fashion%20product%20photography&width=800&height=1000&seq=northtee-detail-4&orientation=portrait'
    ],
    category: 'apparel',
    subcategory: 'tees',
    sizes: ['S', 'M', 'L', 'XL', '2XL', '3XL'],
    colors: [
      { name: 'Black', hex: '#000000' },
      { name: 'White', hex: '#f5f5f5' },
      { name: 'Ash Gray', hex: '#6b6b6b' }
    ],
    inStock: true,
    sku: 'BP-NTS-BK-006'
  },
  {
    id: 7,
    name: 'Hustle Graphic Tee',
    price: 50,
    description: 'For those who move with intention. The Hustle Graphic Tee carries a bold, oversized statement print that speaks to the mentality BillPlay was built on. Ambition, drive, and an unshakeable work ethic — on a tee.',
    highlights: [
      'Heavyweight 260gsm ringspun cotton for premium feel',
      'Bold oversized front and back typography print',
      'Water-based ink for soft hand and lasting color',
      'Dropped shoulder construction for streetwear silhouette',
      'Tagless interior for all-day comfort'
    ],
    materials: '100% Ringspun cotton — 260gsm',
    fit: 'Oversized. Size down one size for a relaxed fit. Model is 5\'11" wearing size M.',
    care: 'Machine wash cold, inside out. Air dry recommended. Do not iron over print.',
    images: [
      'https://readdy.ai/api/search-image?query=black%20oversized%20t-shirt%20with%20bold%20white%20typography%20hustle%20print%20front%20view%20on%20model%20in%20urban%20setting%20dramatic%20lighting%20dark%20background%20editorial%20streetwear%20photography&width=800&height=1000&seq=hustletee-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20oversized%20graphic%20tee%20back%20view%20showing%20bold%20typography%20print%20on%20model%20in%20studio%20with%20dramatic%20lighting%20dark%20background%20editorial%20fashion%20photography%20streetwear&width=800&height=1000&seq=hustletee-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20graphic%20tee%20bold%20typography%20print%20and%20heavyweight%20cotton%20texture%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=hustletee-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20graphic%20t-shirt%20hanging%20on%20minimalist%20rack%20showing%20full%20oversized%20silhouette%20with%20dramatic%20studio%20lighting%20clean%20dark%20background%20editorial%20fashion%20photography&width=800&height=1000&seq=hustletee-detail-4&orientation=portrait'
    ],
    category: 'apparel',
    subcategory: 'tees',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    colors: [
      { name: 'Black', hex: '#000000' },
      { name: 'White', hex: '#f5f5f5' }
    ],
    inStock: true,
    sku: 'BP-HGT-BK-007'
  },
  {
    id: 8,
    name: 'Cargo Joggers',
    price: 95,
    description: 'Function meets fashion. The Cargo Joggers pack multiple utility pockets into a sleek, tapered silhouette. Built for movement, styled for the streets — these are the joggers you\'ll reach for every time.',
    highlights: [
      'Six-pocket cargo utility design with snap and zip closures',
      'Tapered leg with adjustable ankle drawcord',
      'Elastic waistband with interior drawcord for custom fit',
      'Durable ripstop nylon blend resists abrasion',
      'BillPlay woven label on side pocket'
    ],
    materials: '65% Nylon, 35% Cotton ripstop blend',
    fit: 'Tapered fit through the leg. True to size. Model is 6\'1" wearing size L.',
    care: 'Machine wash cold. Tumble dry low. Do not dry clean.',
    images: [
      'https://readdy.ai/api/search-image?query=black%20premium%20cargo%20joggers%20with%20multiple%20pockets%20front%20view%20on%20model%20in%20urban%20setting%20dramatic%20lighting%20dark%20background%20editorial%20streetwear%20fashion%20photography&width=800&height=1000&seq=cargo-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20cargo%20joggers%20side%20view%20showing%20pocket%20placement%20and%20tapered%20leg%20on%20model%20in%20studio%20dramatic%20lighting%20dark%20background%20editorial%20fashion%20photography%20streetwear&width=800&height=1000&seq=cargo-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20cargo%20pocket%20snap%20closure%20and%20ripstop%20fabric%20texture%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography%20high%20detail&width=800&height=1000&seq=cargo-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20cargo%20joggers%20hanging%20on%20minimalist%20rack%20showing%20full%20silhouette%20and%20pockets%20with%20dramatic%20studio%20lighting%20clean%20dark%20background%20editorial%20fashion%20photography&width=800&height=1000&seq=cargo-detail-4&orientation=portrait'
    ],
    category: 'apparel',
    subcategory: 'pants',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    colors: [
      { name: 'Black', hex: '#000000' },
      { name: 'Olive', hex: '#4a5240' }
    ],
    inStock: true,
    sku: 'BP-CGJ-BK-008'
  },
  {
    id: 9,
    name: 'Street Sweatpants',
    price: 75,
    description: 'Elevated comfort for every day. The Street Sweatpants are built from heavyweight French terry with a clean, tapered cut that looks sharp whether you\'re out or laid back. Premium basics done right.',
    highlights: [
      'Heavyweight 380gsm French terry cotton blend',
      'Tapered leg with ribbed ankle for a clean finish',
      'Elastic waistband with internal drawcord',
      'Two side pockets and one back zip pocket',
      'Minimal BillPlay branding on left leg'
    ],
    materials: '80% Cotton, 20% Polyester — 380gsm French terry',
    fit: 'Slim tapered fit. True to size. Model is 6\'0" wearing size L.',
    care: 'Machine wash cold, inside out. Tumble dry low.',
    images: [
      'https://readdy.ai/api/search-image?query=black%20tapered%20sweatpants%20with%20minimal%20branding%20front%20view%20on%20model%20in%20urban%20setting%20dramatic%20lighting%20dark%20background%20editorial%20streetwear%20fashion%20photography%20premium&width=800&height=1000&seq=sweat-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20premium%20sweatpants%20side%20view%20showing%20tapered%20silhouette%20and%20ribbed%20ankle%20on%20model%20in%20studio%20dramatic%20lighting%20dark%20background%20editorial%20fashion%20photography&width=800&height=1000&seq=sweat-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20sweatpants%20waistband%20drawcord%20and%20French%20terry%20fabric%20texture%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=sweat-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20tapered%20sweatpants%20hanging%20on%20minimalist%20rack%20showing%20full%20silhouette%20with%20dramatic%20studio%20lighting%20clean%20dark%20background%20editorial%20fashion%20product%20photography&width=800&height=1000&seq=sweat-detail-4&orientation=portrait'
    ],
    category: 'apparel',
    subcategory: 'pants',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    colors: [
      { name: 'Black', hex: '#000000' },
      { name: 'Charcoal', hex: '#3a3a3a' },
      { name: 'Navy', hex: '#1a1a2e' }
    ],
    inStock: true,
    sku: 'BP-SSP-BK-009'
  },
  // ─── FOOTWEAR ─────────────────────────────────────────────
  {
    id: 10,
    name: 'Urban High-Tops',
    price: 145,
    description: 'Elevated streetwear meets premium comfort. The Urban High-Tops feature a sleek silhouette with premium materials and all-day comfort. Designed for those who move with purpose and style.',
    highlights: [
      'Premium leather and suede upper construction',
      'Padded ankle collar for support and comfort',
      'Durable rubber outsole with superior grip',
      'Cushioned insole for all-day wear',
      'Signature BillPlay branding details'
    ],
    materials: 'Premium leather and suede upper / Textile lining / Rubber outsole',
    fit: 'True to size. Half sizes available. Model wears size 10.',
    care: 'Wipe clean with damp cloth. Use leather conditioner as needed. Air dry only.',
    images: [
      'https://readdy.ai/api/search-image?query=premium%20black%20high-top%20sneakers%20with%20white%20sole%20side%20view%20on%20clean%20pedestal%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20footwear%20photography%20style%20high%20detail&width=800&height=1000&seq=hightop-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20high-top%20sneakers%20front%20view%20showing%20laces%20and%20toe%20box%20on%20model%20feet%20in%20urban%20setting%20with%20dramatic%20lighting%20editorial%20footwear%20photography%20style&width=800&height=1000&seq=hightop-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20high-top%20sneaker%20showing%20leather%20texture%20stitching%20and%20branding%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=hightop-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=premium%20high-top%20sneakers%20back%20view%20showing%20heel%20and%20ankle%20support%20on%20clean%20surface%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20footwear%20photography&width=800&height=1000&seq=hightop-detail-4&orientation=portrait'
    ],
    category: 'footwear',
    subcategory: 'high-tops',
    sizes: ['8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12'],
    colors: [
      { name: 'Black/White', hex: '#000000' },
      { name: 'All Black', hex: '#0a0a0a' },
      { name: 'Grey/White', hex: '#6b6b6b' }
    ],
    inStock: true,
    sku: 'BP-UHT-BW-010'
  },
  {
    id: 11,
    name: 'Street Runner',
    price: 125,
    description: 'Built for speed and style. The Street Runner is a sleek low-top sneaker with a modern athletic silhouette. Lightweight, responsive, and clean enough to wear anywhere — from the block to the boardroom.',
    highlights: [
      'Breathable knit and leather panel upper',
      'Lightweight EVA midsole for cushion and energy return',
      'Durable vulcanized rubber outsole',
      'Padded tongue and collar for comfort',
      'Reflective BillPlay logo detail on heel'
    ],
    materials: 'Knit and premium leather upper / EVA midsole / Rubber outsole',
    fit: 'True to size. Model wears size 10.',
    care: 'Spot clean upper with mild soap and water. Air dry. Do not machine wash.',
    images: [
      'https://readdy.ai/api/search-image?query=black%20and%20white%20low-top%20athletic%20sneakers%20with%20modern%20knit%20upper%20side%20view%20on%20clean%20pedestal%20dramatic%20studio%20lighting%20dark%20background%20editorial%20footwear%20photography&width=800&height=1000&seq=runner-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20white%20runner%20sneakers%20front%20angled%20view%20showing%20toe%20box%20and%20knit%20texture%20on%20clean%20surface%20dramatic%20lighting%20dark%20background%20editorial%20footwear%20photography&width=800&height=1000&seq=runner-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20runner%20sneaker%20knit%20texture%20EVA%20sole%20and%20reflective%20heel%20branding%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=runner-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20white%20low-top%20runner%20sneaker%20back%20heel%20view%20on%20clean%20pedestal%20dramatic%20studio%20lighting%20dark%20background%20editorial%20footwear%20photography%20high%20detail&width=800&height=1000&seq=runner-detail-4&orientation=portrait'
    ],
    category: 'footwear',
    subcategory: 'low-tops',
    sizes: ['7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '12'],
    colors: [
      { name: 'Black/White', hex: '#000000' },
      { name: 'White/Black', hex: '#f5f5f5' },
      { name: 'Grey', hex: '#6b6b6b' }
    ],
    inStock: true,
    sku: 'BP-STR-BW-011'
  },
  {
    id: 12,
    name: 'Classic Low',
    price: 110,
    description: 'You can\'t go wrong with a classic. The Classic Low is a clean, minimal low-top silhouette built from premium canvas with a timeless look that pairs with everything in your wardrobe.',
    highlights: [
      'Premium canvas upper with suede toe cap reinforcement',
      'Classic vulcanized rubber outsole for authentic feel',
      'Cushioned OrthoLite insole for all-day comfort',
      'Metal aglets and waxed cotton laces',
      'Embossed BillPlay logo on tongue tab'
    ],
    materials: 'Premium canvas and suede upper / OrthoLite insole / Vulcanized rubber outsole',
    fit: 'True to size. Model wears size 10.',
    care: 'Spot clean with mild soap and damp cloth. Air dry. Avoid soaking in water.',
    images: [
      'https://readdy.ai/api/search-image?query=minimalist%20black%20canvas%20low-top%20sneakers%20with%20white%20vulcanized%20sole%20side%20view%20on%20clean%20pedestal%20dramatic%20studio%20lighting%20dark%20background%20editorial%20footwear%20photography&width=800&height=1000&seq=classic-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=classic%20black%20canvas%20sneakers%20angled%20front%20view%20on%20clean%20surface%20dramatic%20lighting%20dark%20background%20editorial%20footwear%20photography%20minimal%20premium%20style&width=800&height=1000&seq=classic-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20canvas%20sneaker%20suede%20toe%20cap%20and%20vulcanized%20rubber%20sole%20texture%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=classic-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20canvas%20low-top%20sneakers%20pair%20on%20clean%20pedestal%20overhead%20view%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography%20high%20detail&width=800&height=1000&seq=classic-detail-4&orientation=portrait'
    ],
    category: 'footwear',
    subcategory: 'low-tops',
    sizes: ['8', '8.5', '9', '9.5', '10', '10.5', '11', '12'],
    colors: [
      { name: 'Black', hex: '#000000' },
      { name: 'White', hex: '#f5f5f5' }
    ],
    inStock: true,
    sku: 'BP-CLW-BK-012'
  },
  {
    id: 13,
    name: 'Combat Boots',
    price: 185,
    description: 'Tough, stylish, and built to last. The BillPlay Combat Boots are constructed from full-grain leather with a lug sole that commands the room. From the streets to any setting — these boots mean business.',
    highlights: [
      'Full-grain leather upper with welt construction',
      'Durable lug rubber outsole for grip on any surface',
      'Steel shank for structural support and longevity',
      'Cushioned cork footbed that molds to your foot',
      'Speed hook lacing system for quick on/off'
    ],
    materials: 'Full-grain leather upper / Cork footbed / Rubber lug outsole',
    fit: 'True to size. Wide toe box for comfort. Model wears size 10.',
    care: 'Wipe clean. Apply leather conditioner regularly. Polish with matching cream. Store with cedar trees.',
    images: [
      'https://readdy.ai/api/search-image?query=premium%20black%20full-grain%20leather%20combat%20boots%20with%20lug%20sole%20side%20view%20on%20clean%20surface%20dramatic%20studio%20lighting%20dark%20background%20editorial%20footwear%20photography%20high%20detail&width=800&height=1000&seq=combat-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20leather%20combat%20boots%20front%20view%20showing%20lacing%20and%20toe%20box%20on%20clean%20surface%20dramatic%20lighting%20dark%20background%20editorial%20footwear%20photography%20premium&width=800&height=1000&seq=combat-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20leather%20combat%20boot%20welt%20construction%20lug%20sole%20and%20speed%20hooks%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=combat-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20combat%20boots%20back%20view%20showing%20heel%20and%20lug%20sole%20on%20clean%20pedestal%20dramatic%20studio%20lighting%20dark%20background%20editorial%20footwear%20photography%20high%20detail&width=800&height=1000&seq=combat-detail-4&orientation=portrait'
    ],
    category: 'footwear',
    subcategory: 'boots',
    sizes: ['8', '9', '10', '11', '12'],
    colors: [
      { name: 'Black', hex: '#000000' }
    ],
    inStock: true,
    sku: 'BP-CMB-BK-013'
  },
  {
    id: 14,
    name: 'Logo Slides',
    price: 55,
    description: 'Clean, comfortable, and instantly recognizable. The BillPlay Logo Slides are the perfect recovery and casual wear option. Thick EVA footbed, adjustable strap, and a bold logo hit make these an everyday essential.',
    highlights: [
      'Thick contoured EVA footbed for superior cushioning',
      'Adjustable hook-and-loop strap for custom fit',
      'Slip-resistant rubber outsole',
      'Embossed BillPlay logo on strap',
      'Waterproof construction for pool and beach use'
    ],
    materials: 'EVA footbed / Synthetic upper strap / Rubber outsole',
    fit: 'True to size. Model wears size 10.',
    care: 'Rinse with clean water after use. Air dry. Do not machine wash.',
    images: [
      'https://readdy.ai/api/search-image?query=black%20premium%20slides%20with%20white%20embossed%20logo%20strap%20front%20view%20on%20clean%20surface%20dramatic%20studio%20lighting%20dark%20background%20editorial%20footwear%20product%20photography&width=800&height=1000&seq=slides-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20branded%20slides%20side%20view%20showing%20EVA%20footbed%20thickness%20on%20clean%20surface%20dramatic%20lighting%20dark%20background%20editorial%20footwear%20photography%20premium&width=800&height=1000&seq=slides-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20slides%20logo%20embossed%20strap%20and%20EVA%20sole%20texture%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography%20high%20detail&width=800&height=1000&seq=slides-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20slides%20pair%20overhead%20view%20on%20clean%20surface%20dramatic%20studio%20lighting%20dark%20background%20editorial%20footwear%20product%20photography%20minimal&width=800&height=1000&seq=slides-detail-4&orientation=portrait'
    ],
    category: 'footwear',
    subcategory: 'slides',
    sizes: ['7', '8', '9', '10', '11', '12'],
    colors: [
      { name: 'Black/White', hex: '#000000' },
      { name: 'All White', hex: '#f5f5f5' }
    ],
    inStock: true,
    sku: 'BP-LGS-BK-014'
  },
  // ─── ACCESSORIES ──────────────────────────────────────────
  {
    id: 15,
    name: 'Northside Cap',
    price: 35,
    description: 'Wear your roots. The Northside Cap features a structured six-panel construction with a clean embroidered BillPlay logo and an adjustable back strap. A daily driver that goes with everything.',
    highlights: [
      'Structured six-panel design with firm brim',
      'Premium cotton twill construction',
      'Embroidered chenille BillPlay logo on front',
      'Adjustable snapback or strapback closure',
      'Moisture-wicking sweatband'
    ],
    materials: '100% Cotton twill / Polyester sweatband',
    fit: 'One size fits most. Adjustable back closure.',
    care: 'Spot clean with damp cloth and mild soap. Air dry. Do not machine wash.',
    images: [
      'https://readdy.ai/api/search-image?query=premium%20black%20baseball%20cap%20with%20white%20embroidered%20logo%20front%20view%20on%20clean%20pedestal%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=cap-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20structured%20baseball%20cap%20side%20view%20showing%20brim%20and%20profile%20on%20clean%20surface%20dramatic%20lighting%20dark%20background%20editorial%20product%20photography%20premium&width=800&height=1000&seq=cap-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20baseball%20cap%20embroidered%20logo%20and%20cotton%20twill%20texture%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=cap-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20cap%20back%20view%20showing%20adjustable%20snapback%20closure%20on%20clean%20pedestal%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=cap-detail-4&orientation=portrait'
    ],
    category: 'accessories',
    subcategory: 'hats',
    sizes: ['One Size'],
    colors: [
      { name: 'Black', hex: '#000000' },
      { name: 'White', hex: '#f5f5f5' },
      { name: 'Gray', hex: '#6b6b6b' }
    ],
    inStock: true,
    sku: 'BP-NSC-BK-015'
  },
  {
    id: 16,
    name: 'Culture Beanie',
    price: 30,
    description: 'Stay warm, stay sharp. The Culture Beanie is a clean, ribbed knit beanie with a subtle BillPlay woven patch. Simple, premium, and a natural part of any cold-weather fit.',
    highlights: [
      'Heavyweight ribbed knit for warmth and stretch',
      'Woven BillPlay label patch on front',
      'Fleece-lined interior for extra warmth',
      'Cuffed turn-up hem for classic beanie look',
      'One size fits all with comfortable stretch'
    ],
    materials: '60% Acrylic, 40% Merino Wool / Fleece lining',
    fit: 'One size. Stretches to fit all head sizes.',
    care: 'Hand wash cold with mild detergent. Lay flat to dry. Do not machine wash.',
    images: [
      'https://readdy.ai/api/search-image?query=black%20ribbed%20knit%20beanie%20with%20woven%20white%20logo%20patch%20front%20view%20on%20clean%20surface%20dramatic%20studio%20lighting%20dark%20background%20editorial%20accessories%20product%20photography&width=800&height=1000&seq=beanie-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20premium%20beanie%20on%20model%20showing%20fit%20and%20slouch%20style%20in%20studio%20dramatic%20lighting%20dark%20background%20editorial%20fashion%20photography%20accessories&width=800&height=1000&seq=beanie-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20knit%20beanie%20wool%20texture%20and%20woven%20logo%20patch%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography%20high%20detail&width=800&height=1000&seq=beanie-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20knit%20beanie%20cuffed%20sitting%20on%20clean%20surface%20overhead%20view%20dramatic%20studio%20lighting%20dark%20background%20editorial%20accessories%20product%20photography&width=800&height=1000&seq=beanie-detail-4&orientation=portrait'
    ],
    category: 'accessories',
    subcategory: 'hats',
    sizes: ['One Size'],
    colors: [
      { name: 'Black', hex: '#000000' },
      { name: 'Charcoal', hex: '#3a3a3a' }
    ],
    inStock: true,
    sku: 'BP-CLB-BK-016'
  },
  {
    id: 17,
    name: 'Street Backpack',
    price: 95,
    description: 'Carry your essentials with style. The Street Backpack combines functionality with premium design. Multiple compartments, water-resistant materials, and sleek aesthetics make this the perfect everyday carry.',
    highlights: [
      'Water-resistant ballistic nylon construction',
      'Padded laptop sleeve fits up to 15" devices',
      'Multiple interior and exterior pockets',
      'Adjustable padded shoulder straps',
      'Durable YKK zippers throughout'
    ],
    materials: 'Water-resistant ballistic nylon / Leather accents / YKK zippers',
    fit: 'One size. Adjustable straps fit most body types. 20L capacity.',
    care: 'Spot clean with mild soap and water. Air dry. Do not machine wash.',
    images: [
      'https://readdy.ai/api/search-image?query=premium%20black%20backpack%20front%20view%20with%20minimal%20design%20and%20white%20details%20on%20clean%20surface%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography%20style%20high%20detail&width=800&height=1000&seq=backpack-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20backpack%20on%20model%20showing%20fit%20and%20straps%20in%20urban%20setting%20with%20dramatic%20lighting%20editorial%20lifestyle%20photography%20style&width=800&height=1000&seq=backpack-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20backpack%20showing%20zipper%20quality%20pockets%20and%20material%20texture%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=backpack-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=premium%20backpack%20side%20view%20showing%20depth%20and%20compartments%20on%20clean%20surface%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=backpack-detail-4&orientation=portrait'
    ],
    category: 'accessories',
    subcategory: 'bags',
    sizes: ['One Size'],
    colors: [
      { name: 'Black', hex: '#000000' },
      { name: 'Charcoal', hex: '#36454f' }
    ],
    inStock: true,
    sku: 'BP-SBP-BK-017'
  },
  {
    id: 18,
    name: 'Crossbody Bag',
    price: 65,
    description: 'Compact, stylish, and always within reach. The BillPlay Crossbody Bag keeps your essentials secure with a clean silhouette and adjustable strap. Perfect for daily carry without slowing you down.',
    highlights: [
      'Premium pebbled vegan leather exterior',
      'Magnetic snap main closure with zip security pocket',
      'Adjustable and removable crossbody strap',
      'Interior card slots and zip pocket',
      'BillPlay logo embossed on front'
    ],
    materials: 'Vegan leather exterior / Microfiber lining / Metal hardware',
    fit: 'One size. Strap adjusts from 18" to 54" drop.',
    care: 'Wipe clean with damp cloth. Use leather cleaner for deeper cleaning. Air dry.',
    images: [
      'https://readdy.ai/api/search-image?query=black%20premium%20crossbody%20bag%20with%20adjustable%20strap%20front%20view%20on%20clean%20surface%20dramatic%20studio%20lighting%20dark%20background%20editorial%20accessories%20product%20photography&width=800&height=1000&seq=crossbody-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20crossbody%20bag%20on%20model%20showing%20strap%20and%20fit%20in%20urban%20setting%20dramatic%20lighting%20dark%20background%20editorial%20lifestyle%20fashion%20photography&width=800&height=1000&seq=crossbody-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20crossbody%20bag%20embossed%20logo%20and%20pebbled%20leather%20texture%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=crossbody-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20crossbody%20bag%20side%20view%20showing%20depth%20and%20hardware%20on%20clean%20surface%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=crossbody-detail-4&orientation=portrait'
    ],
    category: 'accessories',
    subcategory: 'bags',
    sizes: ['One Size'],
    colors: [
      { name: 'Black', hex: '#000000' },
      { name: 'Charcoal', hex: '#3a3a3a' }
    ],
    inStock: true,
    sku: 'BP-CBB-BK-018'
  },
  {
    id: 19,
    name: 'Chain Necklace',
    price: 75,
    description: 'Stack up. The BillPlay Chain Necklace is a heavyweight sterling silver-plated curb chain with a clean BillPlay pendant. Wear it solo or layer it — either way you\'re making a statement.',
    highlights: [
      'Sterling silver-plated Cuban link chain',
      'Custom BillPlay logo pendant — 18K gold-plated option available',
      'Lobster claw clasp for secure wear',
      'Available in 18", 20", and 24" lengths',
      'Tarnish-resistant coating'
    ],
    materials: 'Sterling silver plating over brass chain / Pendant: zinc alloy with 18K plating',
    fit: 'Available in 18", 20", and 24" lengths. One size pendant.',
    care: 'Store in dry, airtight pouch when not wearing. Wipe with soft cloth. Avoid water and perfume contact.',
    images: [
      'https://readdy.ai/api/search-image?query=silver%20Cuban%20link%20chain%20necklace%20with%20pendant%20displayed%20on%20black%20velvet%20tray%20dramatic%20studio%20lighting%20dark%20background%20editorial%20jewelry%20photography%20high%20detail&width=800&height=1000&seq=chain-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=silver%20chain%20necklace%20on%20male%20model%20neck%20showing%20length%20and%20style%20in%20studio%20dramatic%20lighting%20dark%20background%20editorial%20fashion%20jewelry%20photography&width=800&height=1000&seq=chain-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20silver%20Cuban%20link%20chain%20texture%20and%20lobster%20claw%20clasp%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20jewelry%20product%20photography&width=800&height=1000&seq=chain-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=silver%20chain%20necklace%20with%20logo%20pendant%20flat%20lay%20on%20dark%20surface%20dramatic%20studio%20lighting%20editorial%20jewelry%20photography%20high%20detail%20minimalist&width=800&height=1000&seq=chain-detail-4&orientation=portrait'
    ],
    category: 'accessories',
    subcategory: 'jewelry',
    sizes: ['18"', '20"', '24"'],
    colors: [
      { name: 'Silver', hex: '#c0c0c0' },
      { name: 'Gold', hex: '#d4af37' }
    ],
    inStock: true,
    sku: 'BP-CHN-SV-019'
  },
  {
    id: 20,
    name: 'Statement Ring',
    price: 45,
    description: 'Wear it loud. The BillPlay Statement Ring is a chunky, bold signet-style ring with an engraved BP logo. Crafted from solid brass with a high-polish finish — for those who wear their brand on their hand.',
    highlights: [
      'Chunky signet-style design with engraved BP logo',
      'Solid brass with tarnish-resistant rhodium or gold plating',
      'Polished finish with beveled edge detail',
      'Unisex sizing — available in whole sizes 6–12',
      'Comes in BillPlay branded box, ready to gift'
    ],
    materials: 'Solid brass base / Rhodium or 18K gold plating',
    fit: 'Whole sizes 6 through 12. Measure knuckle for best fit.',
    care: 'Wipe with soft cloth. Store in dry pouch. Avoid harsh chemicals and prolonged water exposure.',
    images: [
      'https://readdy.ai/api/search-image?query=silver%20bold%20signet%20ring%20with%20engraved%20logo%20displayed%20on%20black%20velvet%20dramatic%20studio%20lighting%20dark%20background%20editorial%20jewelry%20photography%20high%20detail&width=800&height=1000&seq=ring-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=silver%20statement%20ring%20on%20male%20hand%20showing%20size%20and%20boldness%20dramatic%20lighting%20dark%20background%20editorial%20fashion%20jewelry%20photography&width=800&height=1000&seq=ring-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20macro%20detail%20of%20ring%20engraving%20high%20polish%20brass%20and%20beveled%20edge%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20jewelry%20product%20photography&width=800&height=1000&seq=ring-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=silver%20statement%20ring%20displayed%20in%20open%20branded%20box%20on%20dark%20surface%20dramatic%20studio%20lighting%20editorial%20jewelry%20product%20photography&width=800&height=1000&seq=ring-detail-4&orientation=portrait'
    ],
    category: 'accessories',
    subcategory: 'jewelry',
    sizes: ['6', '7', '8', '9', '10', '11', '12'],
    colors: [
      { name: 'Silver', hex: '#c0c0c0' },
      { name: 'Gold', hex: '#d4af37' }
    ],
    inStock: true,
    sku: 'BP-STR-SV-020'
  },
  {
    id: 21,
    name: 'Logo Belt',
    price: 55,
    description: 'The finishing touch on any fit. The BillPlay Logo Belt is crafted from full-grain leather with a brushed metal buckle engraved with the BP logo. Clean, confident, and built to last.',
    highlights: [
      'Full-grain leather strap — gets better with age',
      'Brushed metal BP-engraved buckle',
      'Single-prong closure with five adjustment holes',
      'Edge-stitched detail for premium finish',
      'Available in 30"–44" waist sizes'
    ],
    materials: '100% Full-grain leather strap / Zinc alloy buckle with brushed metal finish',
    fit: 'Order your pants waist size or one size up. Standard 5-hole adjustment.',
    care: 'Wipe with leather conditioner every few months. Store rolled, not folded. Avoid prolonged moisture.',
    images: [
      'https://readdy.ai/api/search-image?query=black%20premium%20full-grain%20leather%20belt%20with%20brushed%20metal%20logo%20buckle%20on%20clean%20surface%20dramatic%20studio%20lighting%20dark%20background%20editorial%20accessories%20product%20photography&width=800&height=1000&seq=belt-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20leather%20belt%20on%20model%20pants%20showing%20fit%20and%20buckle%20detail%20in%20studio%20dramatic%20lighting%20dark%20background%20editorial%20fashion%20accessories%20photography&width=800&height=1000&seq=belt-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20metal%20belt%20buckle%20engraving%20and%20edge-stitched%20leather%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography%20high%20detail&width=800&height=1000&seq=belt-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20leather%20belt%20rolled%20and%20displayed%20on%20clean%20surface%20dramatic%20studio%20lighting%20dark%20background%20editorial%20accessories%20product%20photography%20minimal&width=800&height=1000&seq=belt-detail-4&orientation=portrait'
    ],
    category: 'accessories',
    subcategory: 'belts',
    sizes: ['S/M (28"–34")', 'L/XL (34"–44")'],
    colors: [
      { name: 'Black/Silver', hex: '#000000' },
      { name: 'Black/Gold', hex: '#0a0a0a' }
    ],
    inStock: true,
    sku: 'BP-LGB-BK-021'
  },
  {
    id: 22,
    name: 'Crew Socks 3-Pack',
    price: 25,
    description: 'The details matter. The BillPlay Crew Socks 3-Pack keeps your feet fresh with premium cushioned soles and clean logo branding. Three pairs — mix and match colors, stay stocked on the essentials.',
    highlights: [
      'Set of 3 pairs in coordinating colorways',
      'Reinforced heel and toe for durability',
      'Cushioned arch support sole',
      'Ribbed cuff with BillPlay jacquard logo',
      'Moisture-wicking blended yarns for all-day freshness'
    ],
    materials: '78% Cotton, 18% Polyester, 4% Elastane',
    fit: 'One size fits most — fits shoe sizes 7–13.',
    care: 'Machine wash cold. Tumble dry low. Do not bleach.',
    images: [
      'https://readdy.ai/api/search-image?query=premium%20black%20and%20white%20crew%20socks%20three%20pairs%20displayed%20flat%20on%20clean%20dark%20surface%20dramatic%20studio%20lighting%20editorial%20product%20photography%20accessories&width=800&height=1000&seq=socks-detail-1&orientation=portrait',
      'https://readdy.ai/api/search-image?query=black%20crew%20socks%20on%20model%20feet%20with%20sneakers%20showing%20fit%20and%20logo%20branding%20dramatic%20lighting%20dark%20background%20editorial%20fashion%20accessories%20photography&width=800&height=1000&seq=socks-detail-2&orientation=portrait',
      'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20crew%20socks%20jacquard%20logo%20ribbed%20cuff%20and%20cotton%20texture%20with%20dramatic%20studio%20lighting%20dark%20background%20editorial%20product%20photography&width=800&height=1000&seq=socks-detail-3&orientation=portrait',
      'https://readdy.ai/api/search-image?query=three%20pairs%20of%20black%20white%20gray%20crew%20socks%20stacked%20on%20clean%20surface%20dramatic%20studio%20lighting%20dark%20background%20editorial%20accessories%20product%20photography&width=800&height=1000&seq=socks-detail-4&orientation=portrait'
    ],
    category: 'accessories',
    subcategory: 'socks',
    sizes: ['One Size'],
    colors: [
      { name: 'Black/White', hex: '#000000' },
      { name: 'All White', hex: '#f5f5f5' },
      { name: 'Gray Pack', hex: '#6b6b6b' }
    ],
    inStock: true,
    sku: 'BP-CRS-BK-022'
  }
];
